import { useState, useRef, useEffect } from "react";
import { useGeminiChatMutation } from "@/store/api";
import { MessageSquare, X, Send, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface ChatMsg {
  role: "user" | "assistant";
  content: string;
  sources?: { title: string; uri: string }[];
  latency_ms?: number;
}

const STORAGE_KEY = "gemini_chat_history_v1";
const MAX_HISTORY = 20;  // keep last N messages in session storage

/**
 * GeminiChatFAB — floating-action-button + slide-out chat panel mounted
 * globally in the app shell so it's available on every authenticated
 * page. The conversation includes the LIVE trading-bot snapshot
 * (balances, open positions, engine state, BTC breaker) in the system
 * prompt so Gemini can ground every answer.
 *
 * History persists per browser session (sessionStorage), capped to the
 * last MAX_HISTORY turns to keep token usage bounded.
 */
export function GeminiChatFAB() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [history, setHistory] = useState<ChatMsg[]>(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [send, sendState] = useGeminiChatMutation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY,
        JSON.stringify(history.slice(-MAX_HISTORY)));
    } catch {}
  }, [history]);

  useEffect(() => {
    if (open) {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, history.length]);

  async function onSend() {
    const text = input.trim();
    if (!text || sendState.isLoading) return;
    const userMsg: ChatMsg = { role: "user", content: text };
    const next = [...history, userMsg];
    setHistory(next);
    setInput("");
    try {
      const r: any = await send({
        messages: next.map(m => ({ role: m.role, content: m.content })),
      }).unwrap();
      if (r?.error) {
        setHistory([...next, {
          role: "assistant",
          content: `⚠ ${r.error}`,
        }]);
      } else {
        setHistory([...next, {
          role: "assistant",
          content: r?.reply || "(empty)",
          sources: r?.sources || [],
          latency_ms: r?.latency_ms,
        }]);
      }
    } catch (e: any) {
      setHistory([...next, {
        role: "assistant",
        content: `⚠ Request failed: ${e?.data?.detail || e?.message || "unknown"}`,
      }]);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  }

  function clearHistory() {
    setHistory([]);
    sessionStorage.removeItem(STORAGE_KEY);
  }

  return (
    <>
      {/* FAB */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        title={open ? "Close Gemini chat" : "Chat with Gemini (knows your portfolio state)"}
        className={cn(
          "fixed z-50 bottom-5 right-5 rounded-full shadow-lg w-14 h-14",
          "flex items-center justify-center text-white",
          "transition-all hover:scale-110",
          open
            ? "bg-rose-500 hover:bg-rose-600"
            : "bg-gradient-to-br from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600"
        )}
      >
        {open ? <X className="w-6 h-6" /> : <MessageSquare className="w-6 h-6" />}
      </button>

      {/* Slide-out panel */}
      {open && (
        <div className={cn(
          "fixed z-40 bottom-24 right-5 w-[min(440px,calc(100vw-2.5rem))]",
          "h-[min(640px,calc(100vh-8rem))]",
          "bg-bg2 border border-border rounded-lg shadow-2xl",
          "flex flex-col overflow-hidden"
        )}>
          {/* Header */}
          <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border bg-bg">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-400" />
              <span className="text-sm font-semibold">Gemini Pro</span>
              <span className="text-xs text-muted">· grounded with portfolio + search</span>
            </div>
            {history.length > 0 && (
              <button
                onClick={clearHistory}
                className="text-xs text-muted hover:text-fg"
                title="Clear chat history">
                clear
              </button>
            )}
          </div>

          {/* Messages */}
          <div ref={scrollRef}
                className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
            {history.length === 0 && (
              <div className="text-sm text-muted leading-relaxed">
                Ask anything about your trading state. Gemini sees your
                live balances, open positions, engine settings, and the
                BTC catastrophe breaker. Some prompts to try:
                <ul className="mt-2 space-y-1 text-xs">
                  <li>• "Should I close ESPORTS?"</li>
                  <li>• "What's the biggest risk in my portfolio right now?"</li>
                  <li>• "Why are my grid orders not filling?"</li>
                  <li>• "Find me a new BSC alpha worth watching"</li>
                  <li>• "Explain the H verdict — should I buy it?"</li>
                </ul>
              </div>
            )}
            {history.map((m, i) => (
              <div key={i} className={cn(
                "rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words",
                m.role === "user"
                  ? "bg-violet-500/15 border border-violet-500/30 ml-6"
                  : "bg-bg border border-border mr-6"
              )}>
                {m.content}
                {m.sources && m.sources.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-border/40 text-xs space-y-0.5">
                    <div className="text-muted">Sources:</div>
                    {m.sources.slice(0, 5).map((s, j) => (
                      <a key={j} href={s.uri} target="_blank" rel="noreferrer"
                          className="block text-violet-300 hover:underline truncate">
                        {s.title || s.uri}
                      </a>
                    ))}
                  </div>
                )}
                {m.latency_ms != null && (
                  <div className="mt-1 text-[10px] text-muted">
                    {(m.latency_ms / 1000).toFixed(1)}s
                  </div>
                )}
              </div>
            ))}
            {sendState.isLoading && (
              <div className="rounded-lg px-3 py-2 text-sm bg-bg border border-border mr-6 flex items-center gap-2 text-muted">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Thinking… (Gemini Pro + Google Search, usually 5-15s)
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-border bg-bg px-3 py-2">
            <div className="flex gap-2 items-end">
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Ask anything... (Enter to send, Shift+Enter for newline)"
                rows={2}
                className="flex-1 resize-none bg-bg2 text-fg border border-border rounded px-2 py-1.5 text-sm focus:outline-none focus:border-violet-500"
                disabled={sendState.isLoading}
              />
              <button
                onClick={onSend}
                disabled={!input.trim() || sendState.isLoading}
                className={cn(
                  "rounded p-2 transition-colors",
                  input.trim() && !sendState.isLoading
                    ? "bg-violet-500 hover:bg-violet-600 text-white"
                    : "bg-bg2 text-muted cursor-not-allowed"
                )}
                title="Send (Enter)"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
